/**
 * @file: useFullEditionUpgradeRequest.ts
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { useCurrentUser } from '@euler/app/flows/auth/context';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useMemo, useState } from 'react';
import { AiUpgradeFrom } from './AiUpgradeCard';

/**
 * The AI home's upgrade card: available to a store on an edition short of
 * the full one (workshop or AI), never without an edition; asking
 * records the request as a lead for sales and confirms it. A repeat while
 * one is open confirms the same way, since sales has it already.
 */
export function useFullEditionUpgradeRequest() {
  const line = useCurrentUser()?.store.editionProductLine;
  // The edition it upgrades from; null when there is none to upgrade.
  const from: AiUpgradeFrom | null =
    line == null || line === 'full' ? null : line;
  const { storeService } = useServiceFactory();
  const alert = useAlert();
  const [busy, setBusy] = useState(false);

  const request = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await storeService.requestFullEditionUpgrade();
      void alert({
        title: '已收到您的升级申请',
        message: '我们会尽快与您联系。',
      });
    } catch (e) {
      console.error('[ai-home] upgrade request failed', e);
      void alert({
        title: '提交失败',
        message: (e as Error).message || '请稍后重试。',
      });
    } finally {
      setBusy(false);
    }
  }, [alert, busy, storeService]);

  return useMemo(
    () => ({ from, busy, request: () => void request() }),
    [from, busy, request],
  );
}
